import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Sparkles, MapPin, Trophy, Users, Target, Heart, Globe, Search,
  RefreshCw, Plus, Send, Mail, Instagram, ExternalLink, Check, Lock,
  ArrowUpRight, Zap, AlertCircle, AlertTriangle, X, History, ChevronDown,
} from "lucide-react";

const PRO_PLANS = ["pro", "agency"];

const GEO_OPTIONS = [
  { v: "local",    label: "Local",    desc: "City / regional brands" },
  { v: "national", label: "National", desc: "Country-level players" },
  { v: "global",   label: "Global",   desc: "Big international brands" },
  { v: "any",      label: "Any",      desc: "Open to all" },
];

const BRAND_TYPE_OPTIONS = [
  "Athletic apparel", "Footwear", "Supplements", "Nutrition", "Hydration",
  "Recovery tech", "Wearables", "Energy drinks", "Lifestyle", "Eyewear",
  "Audio", "Auto", "Banking/fintech", "Insurance", "Local SMEs",
];

const VALUE_OPTIONS = [
  "Sustainability", "Diversity & inclusion", "Mental health", "Community",
  "Performance", "Authenticity", "Innovation", "Heritage", "Premium",
  "Affordability", "Female-led", "Independent",
];

const TIER_STYLE = {
  hot_lead: { label: "Hot lead", cls: "border-volt text-volt bg-volt/5" },
  warm:     { label: "Warm",     cls: "border-volt/40 text-volt/80" },
  stretch:  { label: "Stretch",  cls: "border-line text-ink-muted" },
};

export default function SponsorFinder({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = PRO_PLANS.includes((user?.plan || "free").toLowerCase());

  const [form, setForm] = useState({
    sport: "", location: "", level: "", audience_size: "",
    target_categories: [], values_interests: [], preferred_brand_types: [],
    geography_preference: "any", avoid_brands: [], notes: "",
  });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [usedMock, setUsedMock] = useState(false);
  const [addedBrandIds, setAddedBrandIds] = useState({});
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadHistory = () => api.get("/ai/sponsor-finder/history").then(({ data }) => setHistory(data || [])).catch(() => {});

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setForm(f => ({
        ...f,
        sport: data.sport || "",
        location: data.location || "",
        level: data.level || "",
        audience_size: data.audience_size || "",
        target_categories: data.target_sponsor_categories || [],
      }));
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));
    if (isPro) loadHistory();
  }, [isPro]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm(f => {
    const list = f[k] || [];
    return { ...f, [k]: list.includes(v) ? list.filter(x => x !== v) : [...list, v] };
  });

  const search = async () => {
    if (!form.sport || !form.location) {
      setError("Sport and location are required to find relevant brands.");
      return;
    }
    setLoading(true); setError(""); setResults(null); setUsedMock(false);
    try {
      const { data } = await api.post("/ai/sponsor-finder", form);
      setResults(data.suggestions || []);
      setUsedMock(!!data.used_mock);
      loadHistory();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const restoreSearch = async (outputId) => {
    setLoading(true); setError(""); setResults(null); setHistoryOpen(false);
    try {
      const { data } = await api.get(`/ai/sponsor-finder/history/${outputId}`);
      const inp = data.input || {};
      setForm(f => ({ ...f, ...inp }));
      setResults(data.suggestions || []);
      setUsedMock(!!data.used_mock);
      setAddedBrandIds({}); // reset add-state for restored view
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const addToCRM = async (s, idx) => {
    try {
      const { data } = await api.post("/brands", {
        brand_name: s.brand_name,
        category: s.category || "",
        website: s.website || "",
        contact_email: s.contact_email || "",
        instagram_handle: s.instagram_handle || "",
        reason_for_fit: s.reason_for_fit || "",
        priority: s.tier === "hot_lead" ? "high" : s.tier === "warm" ? "medium" : "low",
        status: "target",
        notes: `Confidence: ${s.confidence_score || 0}/100\nSourced via AI Sponsor Finder.`,
        pitch_angle_seed: s.pitch_angle || "",
      });
      setAddedBrandIds(m => ({ ...m, [idx]: data.id }));
      return data.id;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return null;
    }
  };

  const generateOutreach = async (s, idx) => {
    let brandId = addedBrandIds[idx];
    if (!brandId) brandId = await addToCRM(s, idx);
    if (brandId) navigate(`/app/outreach?brand=${brandId}`);
  };

  /* ----------- Render: form available to all plans; results vary by tier ----------- */
  const Wrapper = embedded ? Fragment : DashboardLayout;
  return (
    <Wrapper>
      <div className="p-6 md:p-12" data-testid="sponsor-finder-root">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="label-eyebrow inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-volt" /> {isPro ? "Pro feature" : "AI scout"}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2">
              AI Sponsor Finder
            </h1>
            <p className="text-ink-muted mt-2 max-w-2xl text-sm md:text-base">
              {isPro
                ? "Tell us about you. Get ranked, named brand matches with reason-for-fit, pitch angle, and a confidence score for every suggestion."
                : "AI-matched sponsors based on your profile. Free preview shows 3 picks — upgrade to Pro for 10+ matches, pitch angles, contact emails and search history."}
            </p>
          </div>
          {isPro && history.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setHistoryOpen(o => !o)}
                className="btn-ghost-line text-xs inline-flex items-center gap-2"
                data-testid="sf-history-toggle"
              >
                <History className="w-3.5 h-3.5" />
                Recent searches ({history.length})
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
              </button>
              {historyOpen && (
                <div className="absolute right-0 top-full mt-2 w-[360px] bg-bg-surface border border-volt z-30 max-h-[400px] overflow-y-auto" data-testid="sf-history-list">
                  <div className="px-4 py-3 border-b border-line flex justify-between items-center">
                    <div className="label-eyebrow">Your past searches</div>
                    <button onClick={() => setHistoryOpen(false)} className="text-ink-muted hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <ul className="divide-y divide-line">
                    {history.map(h => {
                      const inp = h.input || {};
                      const summary = [inp.sport, inp.location, inp.geography_preference !== "any" ? inp.geography_preference : null]
                        .filter(Boolean).join(" · ");
                      const chips = [...(inp.target_categories || []), ...(inp.values_interests || [])].slice(0, 3);
                      return (
                        <li key={h.id}>
                          <button
                            onClick={() => restoreSearch(h.id)}
                            className="w-full text-left px-4 py-3 hover:bg-volt/5 transition-colors"
                            data-testid={`sf-history-item-${h.id}`}
                          >
                            <div className="flex justify-between items-start gap-2 mb-1">
                              <div className="font-heading text-sm uppercase tracking-tight text-white truncate">{summary || "Search"}</div>
                              <div className="text-[10px] font-mono text-ink-dim flex-shrink-0">{formatHistoryDate(h.created_at)}</div>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {chips.map(c => (
                                <span key={c} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-line text-ink-muted">{c}</span>
                              ))}
                              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-volt/40 text-volt ml-auto">{h.count} matches</span>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* ----- Inputs ----- */}
          <div className="md:col-span-1 space-y-5 surface p-6" data-testid="sponsor-finder-form">
            <div className="label-eyebrow">Your inputs</div>
            <Input Icon={Trophy} label="Sport *" v={form.sport} on={v=>upd("sport", v)} testid="sf-sport" />
            <Input Icon={MapPin} label="Location *" v={form.location} on={v=>upd("location", v)} testid="sf-location" placeholder="City, country" />
            <Input Icon={Trophy} label="Level" v={form.level} on={v=>upd("level", v)} testid="sf-level" placeholder="Semi-pro, amateur, pro" />
            <Input Icon={Users} label="Audience size" v={form.audience_size} on={v=>upd("audience_size", v)} testid="sf-audience" placeholder="e.g. 12K followers across IG + TikTok" />

            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-2"><Globe className="w-3 h-3 text-volt" /> Geography</div>
              <div className="grid grid-cols-2 gap-2">
                {GEO_OPTIONS.map(g => (
                  <button
                    key={g.v}
                    onClick={() => upd("geography_preference", g.v)}
                    data-testid={`sf-geo-${g.v}`}
                    className={`text-left px-3 py-2 border text-xs uppercase tracking-widest transition-colors ${
                      form.geography_preference === g.v ? "border-volt text-volt" : "border-line text-ink-muted hover:border-white/30"
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <ChipGroup Icon={Target} label="Target categories" options={["Apparel","Footwear","Nutrition","Recovery","Tech","Energy","Eyewear","Auto","Lifestyle","Banking"]} values={form.target_categories} on={v=>toggle("target_categories", v)} testid="sf-categories" />
            <ChipGroup Icon={Heart} label="Values & interests" options={VALUE_OPTIONS} values={form.values_interests} on={v=>toggle("values_interests", v)} testid="sf-values" />
            <ChipGroup Icon={Sparkles} label="Preferred brand types" options={BRAND_TYPE_OPTIONS} values={form.preferred_brand_types} on={v=>toggle("preferred_brand_types", v)} testid="sf-types" />

            <div>
              <div className="label-eyebrow mb-2">Anything to avoid? <span className="text-ink-dim normal-case">(comma-separated)</span></div>
              <input
                value={(form.avoid_brands || []).join(", ")}
                onChange={e => upd("avoid_brands", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                placeholder="e.g. Nike, Red Bull"
                data-testid="sf-avoid"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm"
              />
            </div>

            <div>
              <div className="label-eyebrow mb-2">Notes for the AI</div>
              <textarea value={form.notes} onChange={e=>upd("notes", e.target.value)} rows={3} placeholder="Any extra context — recent campaigns, goals, deal-breakers" data-testid="sf-notes" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white resize-none text-sm" />
            </div>

            {error && (
              <div className="border border-[#FF3B30]/40 p-3 text-sm text-[#FF3B30] flex items-start gap-2" data-testid="sf-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
              </div>
            )}

            <button onClick={search} disabled={loading} className="btn-volt w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="sf-search-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? "Searching brands…" : "Find sponsors"}
            </button>
          </div>

          {/* ----- Results ----- */}
          <div className="md:col-span-2">
            {!results && !loading && (
              <div className="surface p-10 text-center" data-testid="sf-empty">
                <Sparkles className="w-10 h-10 text-volt mx-auto mb-4" />
                <h3 className="font-heading text-2xl uppercase tracking-tight">Ready when you are</h3>
                <p className="text-ink-muted text-sm mt-3 max-w-md mx-auto leading-relaxed">
                  Fill in your inputs on the left and hit <span className="text-white">Find sponsors</span>.
                  We'll surface 10+ ranked matches with pitch angles and confidence scores in seconds.
                </p>
              </div>
            )}
            {loading && (
              <div className="surface p-10 text-center" data-testid="sf-loading">
                <RefreshCw className="w-10 h-10 text-volt mx-auto mb-4 animate-spin" />
                <p className="text-ink-muted">Analysing your profile and matching brands…</p>
              </div>
            )}
            {results && results.length === 0 && (
              <div className="surface p-10 text-center" data-testid="sf-no-results">
                <p className="text-ink-muted">No matches found. Try widening your filters.</p>
              </div>
            )}
            {results && results.length > 0 && (
              <div className="space-y-4" data-testid="sf-results">
                {usedMock && (
                  <div className="surface p-4 border-amber-400/40 bg-amber-400/5 flex items-start gap-3" data-testid="sf-mock-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-sm uppercase tracking-tight text-amber-100">AI temporarily degraded — showing curated suggestions</div>
                      <p className="text-xs text-ink-muted mt-1 leading-relaxed">
                        Our AI matcher couldn't run a live search right now (LLM rate-limit or transient error). The results below are from PITCHD's curated brand library — still relevant and brand-discoverable, but not personalised to your latest profile data. <button onClick={search} className="underline text-volt hover:text-white" data-testid="sf-mock-retry">Try again</button> in a moment for a live AI run.
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">{results.length} matches · ranked by fit{!isPro ? " · free preview" : ""}</div>
                  {usedMock && (
                    <div className="text-[10px] uppercase tracking-widest text-amber-400" data-testid="sf-mock-chip">Curated library</div>
                  )}
                </div>
                {results.map((s, i) => (
                  <BrandCard
                    key={`${s.brand_name}-${i}`}
                    s={s}
                    added={!!addedBrandIds[i]}
                    onAdd={() => addToCRM(s, i)}
                    onOutreach={() => generateOutreach(s, i)}
                    idx={i}
                    isPro={isPro}
                  />
                ))}
                {!isPro && (
                  <div className="surface p-6 border-volt/50 bg-volt/5 flex items-start gap-4" data-testid="sf-upgrade-banner">
                    <div className="w-10 h-10 bg-volt text-black flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl uppercase tracking-tight">Unlock the full Sponsor Finder</h3>
                      <p className="text-sm text-ink-muted mt-1 leading-relaxed">
                        Pro unlocks <span className="text-white">10+ ranked matches</span>, <span className="text-white">AI pitch angles</span>,
                        <span className="text-white"> contact emails</span>, <span className="text-white">brand websites + Instagram</span>,
                        and <span className="text-white">saved search history</span>.
                      </p>
                      <Link to="/app/pricing" className="btn-volt inline-flex items-center gap-2 mt-4 text-xs" data-testid="sf-upgrade-cta">
                        <Sparkles className="w-3.5 h-3.5" /> Upgrade to Pro <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <CommercialNotice />
      </div>
    </Wrapper>
  );
}

function BrandCard({ s, added, onAdd, onOutreach, idx, isPro }) {
  const tier = TIER_STYLE[s.tier] || TIER_STYLE.warm;
  return (
    <div className="surface p-6 hover:border-volt/40 transition-colors" data-testid={`sf-card-${idx}`}>
      <div className="flex justify-between items-start gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-2xl uppercase tracking-tight">{s.brand_name}</h3>
            {s.verified && <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-volt text-volt bg-volt/10">Verified</span>}
          </div>
          <div className="text-xs text-ink-muted mt-1">{s.category}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border ${tier.cls}`} data-testid={`sf-card-${idx}-tier`}>
            {tier.label}
          </div>
          <div className="font-mono text-lg mt-1 text-volt" data-testid={`sf-card-${idx}-score`}>{s.confidence_score}/100</div>
        </div>
      </div>

      {s.reason_for_fit && (
        <div className="mb-3">
          <div className="label-eyebrow text-[10px] mb-1">Why it fits</div>
          <p className="text-sm text-white leading-relaxed">{s.reason_for_fit}</p>
        </div>
      )}
      {isPro && s.pitch_angle && (
        <div className="mb-4 border-l-2 border-volt pl-3">
          <div className="label-eyebrow text-[10px] mb-1">Lead with</div>
          <p className="text-sm text-ink-muted leading-relaxed italic">{s.pitch_angle}</p>
        </div>
      )}
      {!isPro && (
        <div className="mb-4 border-l-2 border-volt/40 pl-3" data-testid={`sf-card-${idx}-locked-pitch`}>
          <div className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Lead with · Pro
          </div>
          <p className="text-sm text-ink-dim leading-relaxed italic">AI-generated pitch angle unlocked on Pro.</p>
        </div>
      )}

      {isPro && (
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-ink-muted font-mono">
          {s.website && <a href={`https://${s.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-volt" data-testid={`sf-card-${idx}-web`}><ExternalLink className="w-3 h-3" /> {s.website}</a>}
          {s.contact_email && <a href={`mailto:${s.contact_email}`} className="inline-flex items-center gap-1 hover:text-volt" data-testid={`sf-card-${idx}-email`}><Mail className="w-3 h-3" /> {s.contact_email}</a>}
          {s.instagram_handle && <a href={`https://instagram.com/${s.instagram_handle.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-volt" data-testid={`sf-card-${idx}-ig`}><Instagram className="w-3 h-3" /> @{s.instagram_handle.replace(/^@/, "")}</a>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={onAdd} disabled={added} className={`text-xs inline-flex items-center gap-2 px-4 py-2 border transition-colors ${added ? "border-volt/40 text-volt cursor-default" : "border-line text-white hover:border-volt"}`} data-testid={`sf-card-${idx}-add`}>
          {added ? <><Check className="w-3.5 h-3.5" /> Added to CRM</> : <><Plus className="w-3.5 h-3.5" /> Add to CRM</>}
        </button>
        <button onClick={onOutreach} className="text-xs inline-flex items-center gap-2 px-4 py-2 bg-volt text-black hover:bg-volt/90 transition-colors" data-testid={`sf-card-${idx}-outreach`}>
          <Send className="w-3.5 h-3.5" /> Generate outreach
        </button>
      </div>
    </div>
  );
}

function Input({ Icon, label, v, on, testid, placeholder }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3 h-3 text-volt" />} {label}
      </div>
      <input value={v || ""} onChange={e=>on(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
    </label>
  );
}

function ChipGroup({ Icon, label, options, values, on, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3 h-3 text-volt" />} {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map(o => {
          const active = values?.includes(o);
          return (
            <button
              key={o}
              onClick={() => on(o)}
              data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[11px] uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${
                active ? "border-volt text-volt bg-volt/5" : "border-line text-ink-muted hover:border-white/30"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatHistoryDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / (1000 * 60 * 60);
    if (diffH < 1) return `${Math.max(1, Math.round(diffH * 60))}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}
